import { useEffect, useState } from 'react'
import type { PrimaryWorkAction } from '../model/workActionPolicy'
import { formatTimeDe } from '../model/workSessionTime'
import { PrimaryWorkButton } from './PrimaryWorkButton'

/** How long `Rückgängig` stays offered after a tap. */
const UNDO_WINDOW_MS = 10_000

export interface PhaseActionBarProps {
  action: PrimaryWorkAction
  /** ISO anchor of the running phase — feeds the elapsed chip. */
  elapsedSince?: string | null
  appearance?: 'solid' | 'outline'
  /** Fires the action. The bar owns nothing but its own feedback. */
  onAction: (action: PrimaryWorkAction) => void
  /** `Rückgängig` — omit for actions that cannot be taken back (the sign-off). */
  onUndo?: () => void
  isPending?: boolean
  /** Set when the last attempt failed. The bar then refuses to go quiet. */
  failed?: boolean
  /** Set right after a tap landed, so the bar can confirm it. */
  confirmedAt?: string | null
  className?: string
  /** Forwarded to `PrimaryWorkButton` — the mobile bottom bar, which shares its row with a
   *  secondary action. See that component for what it changes. */
  compact?: boolean
}

/**
 * The primary phase button plus its own feedback, in one place.
 *
 * Deliberately not a toast (the app's toast is for the office's planning bar, where a save closes
 * its own surface): on a phone the row the thumb just pressed is a better place for the answer
 * than a floating overlay. So the bar has three states — button, confirmation with `Rückgängig`, and
 * a failure that does not disappear.
 *
 * That last state is not decoration. The worker has no backfill: if a tap silently fails he
 * walks away believing the session is open, and only the office can repair it. So a failed tap
 * stays on screen with a retry until it lands.
 */
export function PhaseActionBar({
  action,
  elapsedSince,
  appearance = 'solid',
  onAction,
  onUndo,
  isPending = false,
  failed = false,
  confirmedAt,
  className,
  compact = false,
}: PhaseActionBarProps) {
  const [showConfirmation, setShowConfirmation] = useState(false)

  useEffect(() => {
    if (!confirmedAt) return
    setShowConfirmation(true)
    const timer = setTimeout(() => setShowConfirmation(false), UNDO_WINDOW_MS)
    return () => clearTimeout(timer)
  }, [confirmedAt])

  if (failed) {
    return (
      <div className={className}>
        <div
          role="alert"
          aria-live="polite"
          className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2"
        >
          <span className="text-sm font-medium text-red-700">Nicht gesendet</span>
          <button
            type="button"
            onClick={() => onAction(action)}
            className="shrink-0 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white"
          >
            Nochmal versuchen
          </button>
        </div>
      </div>
    )
  }

  if (showConfirmation && confirmedAt) {
    return (
      <div className={className}>
        <div
          role="status"
          aria-live="polite"
          className="flex items-center justify-between gap-3 rounded-lg border border-green-200 bg-green-50 px-3 py-2"
        >
          <span className="text-sm font-medium text-green-700">
            Gespeichert · {formatTimeDe(confirmedAt)}
          </span>
          {onUndo && (
            <button
              type="button"
              onClick={() => {
                setShowConfirmation(false)
                onUndo()
              }}
              className="shrink-0 text-sm font-semibold text-green-700 underline"
            >
              Rückgängig
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <PrimaryWorkButton
      action={action}
      appearance={appearance}
      elapsedSince={elapsedSince}
      onAction={onAction}
      isPending={isPending}
      className={className}
      compact={compact}
    />
  )
}
